import { useEffect, useLayoutEffect, useRef } from 'react';

export interface UseSaveShortcutOptions {
  /** Register on the shortcut stack only while true (page mounted / dialog open). Default true. */
  enabled?: boolean;
  /** Mirror of the visible Save button's !disabled. Gates firing, not registration. Default true. */
  canSave?: boolean;
  /** True when the registering surface is a portalled MUI Dialog. Default false. */
  modal?: boolean;
  /** When set, Escape triggers this handler (mirrors the Cancel button). Page-level surfaces only — MUI dialogs already close on Escape. */
  onCancel?: () => void;
  /** Mirror of the visible Cancel button's !disabled. Default true. */
  canCancel?: boolean;
}

interface SaveShortcutEntry {
  modal: boolean;
  canSave: () => boolean;
  fire: () => void;
  cancel: (() => void) | null;
  canCancel: () => boolean;
}

/** Human-readable save-shortcut label for button tooltips: '⌘S' on macOS, 'Ctrl+S' elsewhere. */
export function saveShortcutLabel(): string {
  const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);
  return isMac ? '⌘S' : 'Ctrl+S';
}

/** Cancel-shortcut label for button tooltips. Same on every platform. */
export function cancelShortcutLabel(): string {
  return 'Esc';
}

// Only ENABLED entries live on the stack; entries push when a surface opens and are
// removed when it closes, so stack order = open order = nesting order. Top = last.
const stack: SaveShortcutEntry[] = [];
// Latch so a second Cmd+S before the deferred fire lands can't double-save — the save
// button's isSaving disable only flips after the next render.
let firePending = false;

const handleEscape = (event: KeyboardEvent) => {
  // Another component already claimed this Escape (MUI Autocomplete closing its popup,
  // an editor dismissing something) — never turn that press into a form cancel.
  if (event.repeat || event.defaultPrevented) return;

  const top = stack[stack.length - 1];
  // Modal entries never handle Escape: MUI dialogs close themselves on Escape, which IS
  // the dialog's cancel. Only the page-level Cancel button gets a synthetic Esc.
  if (!top || top.modal || !top.cancel) return;

  // Any open Modal surface (dialog, menu, select popover) owns Escape — it closes.
  // :not(.MuiModal-hidden) matters: keepMounted modals (e.g. the Layout mobile-nav
  // drawer) sit in the DOM permanently while closed and must not swallow Escape.
  if (document.querySelector('.MuiModal-root:not(.MuiModal-hidden)')) return;
  const target = event.target instanceof Element ? event.target : null;
  if (target?.closest('dialog, [role="dialog"]')) return;

  if (!top.canCancel()) return;

  event.preventDefault();
  top.cancel();
};

const handleKeyDown = (event: KeyboardEvent) => {
  if (event.key === 'Escape') {
    handleEscape(event);
    return;
  }

  // Cmd+S (Mac) / Ctrl+S (Windows/Linux) only — leave Cmd+Shift+S and Cmd+Alt+S alone.
  if (!(event.metaKey || event.ctrlKey) || event.altKey || event.shiftKey) return;
  if (event.key.toLowerCase() !== 's') return;

  // Suppress the browser's Save-page dialog whenever a save surface is active,
  // even when there is nothing to save right now.
  event.preventDefault();

  if (event.repeat || firePending || event.isComposing) return;

  const top = stack[stack.length - 1];
  if (!top) return;

  // MUI Dialog roots exist in the DOM only while open (no keepMounted on these flows);
  // portals append in open order, so the last one is the topmost dialog. Deliberately
  // NOT .MuiModal-root: Select/Menu/Popover also render under a Modal root and must not
  // swallow the page save. :not(.MuiModal-hidden) guards against any future keepMounted
  // dialog lingering closed in the DOM.
  const dialogs = document.querySelectorAll('.MuiDialog-root:not(.MuiModal-hidden)');
  const topDialog = dialogs[dialogs.length - 1] ?? null;
  const target = event.target instanceof Element ? event.target : null;

  if (!top.modal && (topDialog || target?.closest('dialog, [role="dialog"]'))) {
    // An unregistered modal (ConfirmDialog, discard/refresh prompt, …) sits above the page.
    return;
  }
  if (top.modal && (!topDialog || (target !== null && !topDialog.contains(target)))) {
    // A modal entry only fires from within the topmost dialog (a ConfirmDialog can stack
    // on top of a registered edit dialog).
    return;
  }

  if (!top.canSave()) return;

  // JSONForms text/number cells debounce updates (300ms) and flush on blur; blurring here
  // lets React's passive-effect flush commit the pending value before the deferred fire
  // reads state — same sequence a mouse click on the Save button produces. Focus is
  // deliberately NOT restored afterwards: refocusing the field would swallow the
  // browser-back gesture (Cmd+Left just moves the caret inside a text field), while the
  // browser's tab anchor already continues from the blurred field's position.
  const active = document.activeElement;
  if (active instanceof HTMLElement) active.blur();

  firePending = true;
  setTimeout(() => {
    firePending = false;
    if (stack.includes(top)) top.fire();
  }, 0);
};

const register = (entry: SaveShortcutEntry) => {
  if (stack.length === 0) document.addEventListener('keydown', handleKeyDown);
  stack.push(entry);
};

const unregister = (entry: SaveShortcutEntry) => {
  const index = stack.indexOf(entry);
  if (index !== -1) stack.splice(index, 1);
  if (stack.length === 0) document.removeEventListener('keydown', handleKeyDown);
};

/**
 * Registers a Cmd+S (Mac) / Ctrl+S (Windows/Linux) save shortcut — and optionally an
 * Escape cancel shortcut — for the calling surface.
 *
 * All registrations share one document-level listener and a stack: the innermost open
 * surface (page → slide-out panel dialog → grid row dialog) wins. While any surface is
 * registered the browser's Save-page dialog is suppressed, whether or not a save fires.
 *
 * Pages that render a ViewToolbar should use its `onSave`/`saveDisabled` (and
 * `onCancel`/`cancelDisabled`) props instead of calling this directly; dialogs and
 * toolbar-less forms call it themselves.
 */
export function useSaveShortcut(
  onSave: () => void | Promise<void>,
  options: UseSaveShortcutOptions = {},
): void {
  const { enabled = true, canSave = true, modal = false, onCancel, canCancel = true } = options;
  const hasCancel = !!onCancel;

  const onSaveRef = useRef(onSave);
  const canSaveRef = useRef(canSave);
  const onCancelRef = useRef(onCancel);
  const canCancelRef = useRef(canCancel);
  // useLayoutEffect: refs are fresh at every commit, strictly before any macrotask timer
  // (the deferred fire) can read them.
  useLayoutEffect(() => {
    onSaveRef.current = onSave;
    canSaveRef.current = canSave;
    onCancelRef.current = onCancel;
    canCancelRef.current = canCancel;
  });

  useEffect(() => {
    if (!enabled) return;
    const entry: SaveShortcutEntry = {
      modal,
      canSave: () => canSaveRef.current,
      fire: () => void onSaveRef.current(),
      cancel: hasCancel ? () => onCancelRef.current?.() : null,
      canCancel: () => canCancelRef.current,
    };
    register(entry);
    return () => unregister(entry);
  }, [enabled, modal, hasCancel]);
}

/** @internal test-only */
export function _resetSaveShortcutRegistryForTests(): void {
  stack.length = 0;
  firePending = false;
  document.removeEventListener('keydown', handleKeyDown);
}
